// ---------------------------------------------------------------------------
// DJAYMA engine client — thin typed wrapper over the `djayma` Postgres schema
// (migrations 131-137). Reads go through PostgREST/RLS as the signed-in staff
// user; writes are RPCs (SECURITY DEFINER). The schema is exposed in
// supabase/config.toml [api].schemas.
// ---------------------------------------------------------------------------
import { supabase } from "@/lib/supabase";

// The base client is typed to the public schema; the djayma schema is reached
// via .schema("djayma"). Cast keeps this decoupled from generated public types.
type SchemaClient = {
  from: (table: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => any;
};
export const djaymaDb = (supabase as unknown as { schema: (s: string) => SchemaClient }).schema("djayma");

export interface DjaymaAction {
  id: string;
  kind: "reorder_nudge" | "stockout_alert" | "churn_task" | "event_promo";
  status: "open" | "done" | "dismissed";
  payload: Record<string, unknown>;
  created_at: string;
}

export interface StockAvailability {
  product_id: string;
  warehouse_id: string;
  physical_on_hand: number;
  reserved: number;
  available: number;
}

export interface DjaymaRoute {
  id: string;
  zone: string;
  vehicle_ref: string;
  route_date: string;
  blocked: boolean;
}

export interface DjaymaOrderRow {
  id: string;
  status: string;
  channel: string;
  total_xof: number | null;
  payment_status: string;
  created_at: string;
}

/** Open intelligence actions (stockout / reorder / churn / event). */
export async function fetchOpenActions(): Promise<DjaymaAction[]> {
  const { data, error } = await djaymaDb
    .from("actions")
    .select("id,kind,status,payload,created_at")
    .eq("status", "open")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as DjaymaAction[];
}

/** Products at/under reorder point (available <= 0 surfaced first). */
export async function fetchLowStock(): Promise<StockAvailability[]> {
  const { data, error } = await djaymaDb
    .from("v_stock_available")
    .select("product_id,warehouse_id,physical_on_hand,reserved,available")
    .lte("available", 20)
    .order("available", { ascending: true })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as StockAvailability[];
}

/** Routes blocked by the cash-variance Andon. */
export async function fetchBlockedRoutes(): Promise<DjaymaRoute[]> {
  const { data, error } = await djaymaDb
    .from("routes")
    .select("id,zone,vehicle_ref,route_date,blocked")
    .eq("blocked", true)
    .order("route_date", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as DjaymaRoute[];
}

/** Most recent orders across the engine. */
export async function fetchRecentOrders(): Promise<DjaymaOrderRow[]> {
  const { data, error } = await djaymaDb
    .from("orders")
    .select("id,status,channel,total_xof,payment_status,created_at")
    .order("created_at", { ascending: false })
    .limit(25);
  if (error) throw error;
  return (data ?? []) as DjaymaOrderRow[];
}

/** FCFA formatting (no cents, thin-space grouping) — never euros/dollars. */
export function formatFcfa(amount: number | null | undefined): string {
  const n = Math.round(Number(amount ?? 0));
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

// ---------------------------------------------------------------------------
// Epic 5.2 — Finance reconciliation (v_ledger_balances, v_open_incidents,
// payments). All finance-scoped by RLS (migration 138): a non-finance JWT
// reads zero rows rather than erroring the page.
// ---------------------------------------------------------------------------

export interface LedgerBalance {
  account_id: string;
  code: string;
  name: string;
  account_type: "asset" | "liability" | "revenue" | "expense" | "clearing";
  debit_total: number;
  credit_total: number;
  balance: number;
}

export interface OpenIncident {
  id: string;
  kind: string;
  route_id: string | null;
  zone: string | null;
  vehicle_ref: string | null;
  route_date: string | null;
  blocked: boolean | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface RecentPayment {
  id: string;
  order_id: string;
  method: string;
  status: string;
  amount_xof: number;
  provider_ref: string | null;
  webhook_signature_ok: boolean | null;
  created_at: string;
}

/** Trial balance per ledger account (Σbalance = 0 when the books are square). */
export async function fetchLedgerBalances(): Promise<LedgerBalance[]> {
  const { data, error } = await djaymaDb
    .from("v_ledger_balances")
    .select("account_id,code,name,account_type,debit_total,credit_total,balance")
    .order("account_type", { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as LedgerBalance[];
}

/** Unresolved Andon incidents (cash variance, …) with route context. */
export async function fetchOpenIncidents(): Promise<OpenIncident[]> {
  const { data, error } = await djaymaDb
    .from("v_open_incidents")
    .select("id,kind,route_id,zone,vehicle_ref,route_date,blocked,detail,created_at")
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as OpenIncident[];
}

/** Most recent payments (mobile money + cash), for reconciliation. */
export async function fetchRecentPayments(): Promise<RecentPayment[]> {
  const { data, error } = await djaymaDb
    .from("payments")
    .select("id,order_id,method,status,amount_xof,provider_ref,webhook_signature_ok,created_at")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as RecentPayment[];
}

// ---------------------------------------------------------------------------
// Epic 5.3 — Call-center 360 (v_shop_360). Inherits shops RLS: call-center /
// finance / direction / ops see the whole book; an agent sees only theirs.
// ---------------------------------------------------------------------------

export interface Shop360 {
  shop_id: string;
  name: string;
  owner_name: string;
  phone: string;
  whatsapp_phone: string | null;
  shop_type: string;
  tier: string;
  region: string;
  zone: string;
  credit_stage: string;
  whatsapp_opt_in: boolean;
  language_pref: string;
  assigned_agent_id: string | null;
  credit_account_stage: string | null;
  limit_xof: number | null;
  exposure_xof: number | null;
  overdue_count: number | null;
  score: number | null;
  credit_updated_at: string | null;
  order_count: number;
  last_order_at: string | null;
  open_action_count: number;
}

const SHOP_360_COLUMNS =
  "shop_id,name,owner_name,phone,whatsapp_phone,shop_type,tier,region,zone," +
  "credit_stage,whatsapp_opt_in,language_pref,assigned_agent_id," +
  "credit_account_stage,limit_xof,exposure_xof,overdue_count,score,credit_updated_at," +
  "order_count,last_order_at,open_action_count";

/**
 * Search the shop book for the call center. Empty query returns the most
 * recently active shops; a query matches name / owner / phone (case-insensitive).
 */
export async function searchShops360(query: string): Promise<Shop360[]> {
  const q = query.trim();
  let builder = djaymaDb.from("v_shop_360").select(SHOP_360_COLUMNS);
  if (q.length > 0) {
    const like = `%${q}%`;
    builder = builder.or(`name.ilike.${like},owner_name.ilike.${like},phone.ilike.${like}`);
  }
  const { data, error } = await builder
    .order("last_order_at", { ascending: false, nullsFirst: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as Shop360[];
}

// ---------------------------------------------------------------------------
// Operational consoles (migration 140). Each is a SECURITY DEFINER read fn
// returning jsonb, so it works without the app_role claim wiring. cost/margin
// are never returned by these.
// ---------------------------------------------------------------------------
async function callConsole<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await djaymaDb.rpc(fn, args ?? {});
  if (error) throw error;
  return (data ?? null) as T;
}

export interface ConsoleStockRow {
  warehouse_id: string;
  warehouse: string;
  sku: string;
  product: string;
  brand: string;
  available: number;
  reorder_point: number;
  min_stock: number;
  low: boolean;
}
/** Epic 1 — net available per warehouse × product with reorder flag. */
export function fetchConsoleStock(warehouseId?: string): Promise<ConsoleStockRow[]> {
  return callConsole<ConsoleStockRow[]>("fn_console_stock", { p_warehouse: warehouseId ?? null }).then((r) => r ?? []);
}

export interface ConsoleOrderRow {
  id: string;
  status: string;
  channel: string;
  payment_status: string;
  payment_method: string | null;
  total_xof: number | null;
  created_at: string;
  shop_name: string;
  zone: string;
  region: string;
  item_count: number;
}
/** Epic 3 — order lifecycle board. */
export function fetchConsoleOrders(limit = 100): Promise<ConsoleOrderRow[]> {
  return callConsole<ConsoleOrderRow[]>("fn_console_orders", { p_limit: limit }).then((r) => r ?? []);
}

export interface ConsoleRouteRow {
  id: string;
  route_date: string;
  zone: string;
  vehicle_ref: string;
  blocked: boolean;
  driver: string;
  stops: number;
  completed: number;
  failed: number;
  pending: number;
}
/** Logistics — routes with per-status delivery counts. */
export function fetchConsoleRoutes(date?: string): Promise<ConsoleRouteRow[]> {
  return callConsole<ConsoleRouteRow[]>("fn_console_routes", { p_date: date ?? null }).then((r) => r ?? []);
}

export interface ConsolePriceRow {
  sku: string;
  product: string;
  brand: string;
  shop_type: string;
  price_xof: number;
  effective_from: string;
  approved: boolean;
}
/** Epic 2 — customer price book (no cost/margin). */
export function fetchConsolePricing(): Promise<ConsolePriceRow[]> {
  return callConsole<ConsolePriceRow[]>("fn_console_pricing").then((r) => r ?? []);
}

export interface ConsolePromoRow {
  id: string;
  name: string;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  target_shop_types: string[] | null;
  target_zones: string[] | null;
  max_redemptions_per_shop: number;
  finance_approved: boolean;
  direction_approved: boolean;
}
/** Epic 2 — promotions board (status + window + approvals). */
export function fetchConsolePromos(): Promise<ConsolePromoRow[]> {
  return callConsole<ConsolePromoRow[]>("fn_console_promos").then((r) => r ?? []);
}

export interface ConsolePartnerRow {
  id: string;
  status: string;
  sla_deadline: string;
  accepted_at: string | null;
  dispatched_at: string | null;
  completed_at: string | null;
  sla_breached: boolean;
  hub: string;
  total_xof: number | null;
  shop_name: string;
  zone: string;
}
/** Epic 8 — partner / wholesaler fulfillment board with SLA. */
export function fetchConsolePartners(): Promise<ConsolePartnerRow[]> {
  return callConsole<ConsolePartnerRow[]>("fn_console_partners").then((r) => r ?? []);
}

export interface ConsoleChannels {
  channels: { channel: string; orders: number; revenue_xof: number }[];
  whatsapp_open: number;
}
/** Epic 6 — order mix by channel + open WhatsApp session windows. */
export function fetchConsoleChannels(): Promise<ConsoleChannels> {
  return callConsole<ConsoleChannels>("fn_console_channels").then(
    (r) => r ?? { channels: [], whatsapp_open: 0 }
  );
}

export interface ConsoleUserRow {
  id: string;
  role: string;
  full_name: string;
  phone: string | null;
  agent_code: string | null;
  is_active: boolean;
  shop_name: string | null;
  supplier_name: string | null;
}
/** Epic 0.3 — access / users directory. */
export function fetchConsoleUsers(): Promise<ConsoleUserRow[]> {
  return callConsole<ConsoleUserRow[]>("fn_console_users").then((r) => r ?? []);
}
